import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../context/SocketContext';
import { AlertCircle, AlertTriangle, ArrowRight, X } from 'lucide-react';

export interface LowStockAlertEvent {
    ingredientId: string;
    internalCode: string;
    displayName: string;
    currentStock: number;
    lowStockThreshold: number;
    unit: string;
    severity: 'WARNING' | 'CRITICAL';
    region: 'PK' | 'UAE';
    timestamp: string;
}

export const RealTimeLowStockBanner: React.FC = () => {
    const { socket, connected } = useSocket();
    const navigate = useNavigate();
    const [alerts, setAlerts] = useState<LowStockAlertEvent[]>([]);

    useEffect(() => {
        if (!socket) return;

        // Auto-join regional admin telemetry room (default to pk if not explicitly set)
        const region = (localStorage.getItem('admin_region') || 'pk').toLowerCase();
        socket.emit('join_admin_regional', region);

        const handleLowStock = (data: LowStockAlertEvent) => {
            setAlerts((prev) => {
                // Deduplicate by ingredientId, keeping latest
                const filtered = prev.filter(a => a.ingredientId !== data.ingredientId);
                return [data, ...filtered];
            });
        };

        socket.on('inventory:low_stock', handleLowStock);

        return () => {
            socket.off('inventory:low_stock', handleLowStock);
        };
    }, [socket, connected]);

    if (alerts.length === 0) return null;

    const currentAlert = alerts[0];
    const isCritical = currentAlert.severity === 'CRITICAL';

    const handleDismiss = (id: string) => {
        setAlerts((prev) => prev.filter(a => a.ingredientId !== id));
    };

    const handleCreatePO = () => {
        navigate(`/procurement/orders/new?ingredientId=${currentAlert.ingredientId}&ingredientCode=${encodeURIComponent(currentAlert.internalCode)}`);
    };

    return (
        <div 
            className={`w-full px-4 py-2.5 text-sm font-medium flex items-center justify-between shadow-md transition-all duration-300 ${
                isCritical 
                    ? 'bg-red-600 text-white animate-pulse' 
                    : 'bg-amber-500 text-slate-950'
            }`}
        >
            <div className="flex items-center gap-3">
                {isCritical ? (
                    <AlertCircle className="w-5 h-5 flex-shrink-0 text-white" />
                ) : (
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 text-slate-950" />
                )}
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold tracking-wide uppercase px-2 py-0.5 rounded bg-black/20 text-xs">
                        {isCritical ? 'Critical Stock Deficit' : 'Low Stock Warning'}
                    </span>
                    <span>
                        <strong>{currentAlert.displayName || currentAlert.internalCode}</strong> is running low in {currentAlert.region}: 
                        <span className="underline ml-1">
                            {currentAlert.currentStock} {currentAlert.unit} remaining
                        </span> 
                        {' '}(Threshold: {currentAlert.lowStockThreshold} {currentAlert.unit})
                    </span>
                    {alerts.length > 1 && (
                        <span className="text-xs opacity-80">
                            (+{alerts.length - 1} more alert{alerts.length > 2 ? 's' : ''})
                        </span>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-3">
                <button
                    onClick={handleCreatePO}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-semibold shadow-sm transition-colors ${
                        isCritical 
                            ? 'bg-white text-red-700 hover:bg-red-50' 
                            : 'bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                >
                    Generate PO
                    <ArrowRight className="w-3.5 h-3.5" />
                </button>
                <button
                    onClick={() => handleDismiss(currentAlert.ingredientId)}
                    className="p-1 rounded hover:bg-black/10 transition-colors"
                    title="Dismiss"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

export default RealTimeLowStockBanner;
